/**
 * SectionHeading — the shared red-accent section title used across the app:
 * a rounded brand-red accent bar, an uppercase h2 title (the global h2 brand
 * treatment), and a full-width hairline divider beneath. Use this for EVERY
 * "accent bar + title + underline" heading (intake form sections, dashboard
 * panels, …) so they render identically.
 *
 *   <SectionHeading id="chart-heading">Submissions by Stage</SectionHeading>
 *   <SectionHeading action={<Link…>View all</Link>}>Recent Activity</SectionHeading>
 */

import { Text, makeStyles, mergeClasses, tokens } from '@fluentui/react-components';
import type { KeyboardEvent, ReactNode } from 'react';
import { brand } from '@/shared/styles/colors';

const useStyles = makeStyles({
  row: {
    display: 'flex',
    alignItems: 'center',
    columnGap: tokens.spacingHorizontalS,
    paddingBottom: tokens.spacingVerticalS,
    marginBottom: tokens.spacingVerticalL,
    borderBottomWidth: tokens.strokeWidthThin,
    borderBottomStyle: 'solid',
    borderBottomColor: tokens.colorNeutralStroke2,
  },
  // Rounded brand-red accent bar; stretches to the title height so its top and
  // bottom are rounded and it never intersects the divider below.
  accent: {
    flexShrink: 0,
    width: '4px',
    alignSelf: 'stretch',
    minHeight: '1.25rem',
    backgroundColor: brand.red,
    borderRadius: tokens.borderRadiusSmall,
  },
  title: {
    marginTop: 0,
    marginBottom: 0,
    minWidth: 0,
    flexGrow: 1,
  },
  action: {
    flexShrink: 0,
  },
  // When the whole header toggles a section: clickable affordance.
  clickable: {
    cursor: 'pointer',
    userSelect: 'none',
    ':hover': { color: tokens.colorNeutralForeground2 },
  },
});

export interface SectionHeadingProps {
  children: ReactNode;
  /** id applied to the heading text, for `aria-labelledby` references. */
  id?: string;
  /** Optional right-aligned action, e.g. a "View all" link. */
  action?: ReactNode;
  /** Extra class on the row (e.g. flex-shrink control inside a flex column). */
  className?: string;
  /**
   * When provided, the ENTIRE header row becomes a toggle: click or
   * Enter/Space anywhere on the bar fires this. Pair with `expanded` for the
   * correct aria state. (Keep `action` non-interactive in this mode to avoid
   * nested controls.)
   */
  onClick?: () => void;
  /** aria-expanded state when the header is a toggle (see `onClick`). */
  expanded?: boolean;
}

export function SectionHeading({ children, id, action, className, onClick, expanded }: SectionHeadingProps) {
  const styles = useStyles();
  const interactive = onClick
    ? {
        role: 'button',
        tabIndex: 0,
        'aria-expanded': expanded,
        onClick,
        onKeyDown: (e: KeyboardEvent) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            onClick();
          }
        },
      }
    : {};
  return (
    <div
      className={mergeClasses(styles.row, onClick && styles.clickable, className)}
      {...interactive}
    >
      <span className={styles.accent} aria-hidden="true" />
      <Text as="h2" size={400} weight="semibold" id={id} className={styles.title}>
        {children}
      </Text>
      {action != null && <span className={styles.action}>{action}</span>}
    </div>
  );
}

export default SectionHeading;
